import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { tokenManager } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

// JWT 페이로드 디코딩 (base64)
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export default function ImpersonatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { fetchUser, setImpersonation } = useAuthStore();

  useEffect(() => {
    const processToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('빙의 토큰이 없습니다.');
        return;
      }

      // JWT 페이로드 디코딩
      const payload = decodeJwtPayload(token);
      if (!payload) {
        setError('유효하지 않은 토큰입니다.');
        return;
      }

      // 빙의 토큰인지 확인
      if (payload.type !== 'impersonation') {
        setError('빙의 토큰이 아닙니다.');
        return;
      }

      // 빙의 토큰은 sessionStorage에 저장 (탭별 격리)
      tokenManager.setImpersonationToken(token);

      try {
        // 사용자 정보 조회
        await fetchUser();

        // 빙의 정보 설정
        const user = useAuthStore.getState().user;
        setImpersonation({
          adminId: payload.adminId as string,
          adminEmail: payload.adminEmail as string,
          targetUserId: payload.sub as string,
          targetUserName: user?.name || '알 수 없음',
        });

        // 대시보드로 이동
        navigate('/', { replace: true });
      } catch {
        setError('사용자 정보를 불러오는데 실패했습니다.');
        tokenManager.clearImpersonationToken();
      }
    };

    processToken();
  }, [searchParams, navigate, fetchUser, setImpersonation]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-100">
        <div className="bg-white p-8 rounded-card shadow-md max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-ink-900 mb-2">빙의 실패</h1>
          <p className="text-ink-600 mb-4">{error}</p>
          <Button onClick={() => window.close()}>창 닫기</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-100">
      <div className="bg-white p-8 rounded-card shadow-md max-w-md w-full text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
        <h1 className="text-xl font-bold text-ink-900 mb-2">빙의 모드 진입 중...</h1>
        <p className="text-ink-600">잠시만 기다려주세요.</p>
      </div>
    </div>
  );
}
